// src/components/Order/Order.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Container,
  Heading,
  Input,
  ButtonBase,
  Select,
  Error,
  Table,
  LoadingSpinner,
  LoadingContainer,
  StatusBadge,
  FormContainer,
  SalesItemsContainer,
  FieldContainer,
  CheckboxContainer,
  StyledTextarea,
  SideBySideContainer,
  StockCounter,
  PlusButton,
  ExpandButton,
  QuantityContainer,
  QuantityText,
} from "../../styled/common";
import { FaEdit, FaAngleUp } from "react-icons/fa"; // Import the icons you're using
import { useParams } from "react-router-dom";
import styled from 'styled-components';

const SaveButton = styled(ButtonBase)`
  margin-top: 10px;
  width: 100%;
`;

const Order = ({ propOrderNo, user }) => {
  const { paramOrderNo } = useParams();
  const orderNo = propOrderNo || paramOrderNo;

  const [order, setOrder] = useState(null);
  const [salesOrderItems, setSalesOrderItems] = useState([]);
  const [inventoryData, setInventoryData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState("");
  const [selectedAction, setSelectedAction] = useState("");
  const [quantity, setQuantity] = useState("");
  const [important, setImportant] = useState(false);
  const [comments, setComments] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!orderNo) {
        setError("Order number is missing.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const orderResponse = await axios.get(`/api/purchaseorders?orderno=${orderNo}`);
        const order = orderResponse.data[0];

        setOrder(order);
        setSalesOrderItems(order?.salesOrders);
        setImportant(order?.importantFlag || false);
        setComments(order?.comments || "");

        if (user.role === "tech" || user.role === "warehouseAdmin") {
          const inventoryResponse = await axios.get(`/api/inventory?tech_id=${user.id}`);
          const inventoryMap = {};

          for (const item of order?.salesOrders || []) {
            const materialResponse = await axios.get(`/api/materials?materialid=${item["Material"]}`);
            const material = materialResponse.data[0];

            if (material && material._id) {
              inventoryMap[material.Material] = inventoryResponse.data.find(
                (inv) => inv.material_id._id === material._id
              )?.totalWorkingStockOnHand || 0;
            }
          }
          setInventoryData(inventoryMap);
        }
      } catch (err) {
        console.error("Error fetching order details:", err);
        setError("Failed to load order details. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderNo, user.role, user.id]);

  const handleImportantChange = async () => {
    try {
      const updatedImportantFlag = !important;
      setImportant(updatedImportantFlag);

      await axios.put(`/api/purchaseorders/${order._id}/important`, {
        importantFlag: updatedImportantFlag,
      });

      alert("Order importance updated successfully!");
    } catch (error) {
      console.error("Error updating importance:", error);
      alert("Failed to update importance. Please try again.");
    }
  };

  const handleSaveComments = async () => {
    try {
      await axios.put(`/api/purchaseorders/${order._id}/comments`, { comments });
      await fetchUpdatedData();
      alert("Updated successfully!");
    } catch (err) {
      console.error("Error fetching updated data:", err);
      alert("Failed to load updated data. Please try again later.");
    }
  };

  const handleSave = async () => {
    try {
      // Clear any previous errors
      setError(null);

      // Validation for required fields
      if (!selectedItem) {
        setError("Please select a material.");
        return;
      }

      if (!selectedAction) {
        setError("Please select an action type.");
        return;
      }

      if (selectedItem !== "all" && (!quantity || isNaN(quantity) || quantity <= 0)) {
        setError("Please enter a valid quantity greater than 0.");
        return;
      }

      let statusField;
      if (selectedAction === "Received") {
        statusField = "Received Status";
      } else if (selectedAction === "Collected") {
        statusField = "Collection Status";
      } else if (selectedAction === "Utilised") {
        statusField = "Utilisation Status";
      } else {
        alert("Invalid action type selected.");
        return;
      }
  
      if (selectedItem === "all") {
        // Perform stock check for all materials first
        const failedItems = [];
        const updateDataList = salesOrderItems.map((item) => {
          const qtyOrdered = item["Ordered Qty"];
          const currentQty = totalQuantityByItem(item, statusField);
          const difference = qtyOrdered - currentQty;
  
          if (selectedAction === "Utilised") {
            const availableStock = inventoryData[item["Material"]] || 0;
            if (difference > availableStock) {
              failedItems.push(item["Material"]);
            }
          }
  
          if (difference > 0) {
            return {
              salesOrderItemId: item._id,
              statusField,
              actionData: {
                tech_id: user.id,
                quantity: difference,
              },
            };
          }
          return null; // Skip materials with no update needed
        });
  
        // Abort if any material fails the stock check
        if (failedItems.length > 0) {
          alert(
            `Bulk ${selectedAction.toLowerCase()} fulfilment failed due to insufficient stock for materials: ${failedItems.join(
              ", "
            )}.`
          );
          return; // Exit early without making any updates
        }
  
        // Proceed to perform updates if all checks pass
        const updatePromises = updateDataList
          .filter((data) => data !== null) // Exclude null entries
          .map((updateData) =>
            axios.put(`/api/salesorders`, updateData) // Send update requests
          );
  
        await Promise.all(updatePromises);
        
        // Reset form and fetch updated data
        setSelectedItem("");
        setSelectedAction("");
        setQuantity("");
        
        await fetchUpdatedData();

        alert(`All materials updated for ${selectedAction}!`);
      } else {
        // Handle single material update
        const updateData = {
          salesOrderItemId: selectedItem,
          statusField,
          actionData: {
            tech_id: user.id,
            quantity: Number(quantity),
          },
        };
  
        await axios.put(`/api/salesorders`, updateData);

        // Reset form and fetch updated data
        setSelectedItem("");
        setSelectedAction("");
        setQuantity("");
        
        await fetchUpdatedData();
        alert("Updated successfully!");
      }
    } catch (error) {
      console.error("Error updating data:", error);
      if (error.response?.data?.error) {
        alert(`Failed to update data: ${error.response.data.error}`);
      } else {
        alert("Failed to update data. Please try again.");
      }
      // Reset form and fetch updated data
      setSelectedItem("");
      setSelectedAction("");
      setQuantity("");
      
      await fetchUpdatedData();
    }
  };  

  const handleQuantityUpdate = async (item, actionType) => {
    const confirmed = window.confirm(`Are you sure you want to increment the ${actionType.toLowerCase()} quantity by 1?`);
    if (!confirmed) return;

    try {
      const statusField = actionType === "Received" ? "Received Status" : actionType === "Collected" ? "Collection Status" : "Utilisation Status";
  
      const updateData = {
        salesOrderItemId: item._id,
        statusField,
        actionData: {
          tech_id: user.id,
          quantity: 1, // increment by 1
        },
      };
  
      await axios.put(`/api/salesorders`, updateData);
      alert(`${actionType} status updated successfully!`);
  
      await fetchUpdatedData(); // Fetch the updated data to reflect changes
    } catch (error) {
      console.error("Error updating data:", error);
      if (error.response && error.response.data && error.response.data.error) {
        alert(`Failed to update data: ${error.response.data.error}`);
      } else {
        alert("Failed to update data. Please try again.");
      }
    }
  };  

  const fetchUpdatedData = async () => {
    try {
      const orderResponse = await axios.get(`/api/purchaseorders?orderno=${orderNo}`);
      const order = orderResponse.data[0] || {};
  
      setOrder(order);
      setSalesOrderItems(order?.salesOrders || []);
      setImportant(order?.importantFlag || false);
  
      if (user.role === "tech" || user.role === "warehouseAdmin") {
        const inventoryResponse = await axios.get(`/api/inventory?tech_id=${user.id}`);
        const inventoryMap = {};
  
        for (const item of order.salesOrders || []) {
          const materialResponse = await axios.get(`/api/materials?materialid=${item["Material"]}`);
          const material = materialResponse.data[0];
  
          if (material && material._id) {
            inventoryMap[material.Material] = inventoryResponse.data.find(
              (inv) => inv.material_id._id === material._id
            )?.totalWorkingStockOnHand || 0;
          }
        }
  
        setInventoryData(inventoryMap);
      }
    } catch (err) {
      console.error("Error fetching updated data:", err);
      setError("Failed to load updated data. Please try again later.");
    }
  };

  const totalQuantityByItem = (item, status) =>
    item[status].reduce((sum, action) => sum + action.quantity, 0);

  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
      </LoadingContainer>
    );
  }

  return (
    <Container>
      {order ? (
        <>
          <Heading>Order Details - {order["PO Number"]}</Heading>

          <CheckboxContainer>
                <input type="checkbox" checked={important} onChange={handleImportantChange} />
                <label>Mark as Important</label>
          </CheckboxContainer>

          <ExpandButton onClick={toggleExpand} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {isExpanded ? <FaAngleUp /> : <FaEdit />}
            {isExpanded ? "Collapse" : "Update Order Details"}
          </ExpandButton>

          {/* Expandable Section */}
          {isExpanded && (
            <>
              <SideBySideContainer>
                {(user.role === "tech" || user.role === "warehouseAdmin") && (
                  <FormContainer>
                  <Heading>Update Quantity (Bulk)</Heading>
                  {error && <Error>{error}</Error>}

                  <FieldContainer>
                    <label>Select Material:</label>
                    <Select
                      value={selectedItem}
                      onChange={(e) => setSelectedItem(e.target.value)}
                    >
                      <option value="" disabled>
                        Choose a Material
                      </option>
                      <option value="all">All Materials</option>
                      {salesOrderItems.map((item) => (
                        <option key={item._id} value={item._id}>
                          {item["Material"]} - {item["Material Description"]}
                        </option>
                      ))}
                    </Select>
                  </FieldContainer>

                  <FieldContainer>
                    <label>Select Action:</label>
                    <Select
                      value={selectedAction}
                      onChange={(e) => setSelectedAction(e.target.value)}
                    >
                      <option value="" disabled>
                        Choose action
                      </option>
                      <option value="Received">Received</option>
                      <option value="Collected">Collected</option>
                      <option value="Utilised">Utilised</option>
                    </Select>
                  </FieldContainer>

                  {selectedItem !== "all" && (
                    <FieldContainer>
                      <label>Enter Quantity:</label>
                      <Input
                        type="number"
                        min="0"
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        placeholder="Enter quantity"
                      />
                    </FieldContainer>)}

                  <SaveButton variant="primary" type="button" onClick={handleSave}>
                    Save
                  </SaveButton>
                </FormContainer>
                )}

                <FormContainer>
                  <Heading>Comments</Heading>
                  <FieldContainer>
                    <StyledTextarea
                      value={comments}
                      onChange={(e) => {
                        setComments(e.target.value);
                      }}
                      placeholder="Enter comments"
                    />

                    <ButtonBase
                      variant="secondary"
                      type="button"
                      onClick={handleSaveComments}
                    >
                      Save
                    </ButtonBase>
                  </FieldContainer>
                </FormContainer>

              </SideBySideContainer>
            </>
          )}

          <SalesItemsContainer>
            <Heading>Overview of Quantities Per Item</Heading>
            <Table>
              <thead>
                <tr>
                  <th>Material</th>
                  <th>NMO</th>
                  <th>Qty Ordered</th>
                  <th>
                    Qty Received
                    <span style={{ marginLeft: "5px" }}>
                      <StatusBadge status={order.receivedStatus.toLowerCase()} />
                    </span>
                  </th>
                  <th>
                    Qty Collected
                    <span style={{ marginLeft: "5px" }}>
                    <StatusBadge status={order.collectionStatus.toLowerCase()} />
                    </span>
                  </th>
                  <th>
                    Qty Utilised
                    <span style={{ marginLeft: "5px" }}>
                    <StatusBadge status={order.utilisationStatus.toLowerCase()} />
                    </span>
                  </th>
                  {(user.role === "tech" || user.role === "warehouseAdmin") && <th>Stock on Hand</th>}
                </tr>
              </thead>
              <tbody>
                {salesOrderItems.map((item) => (
                  <tr key={item._id}>
                    <td data-label="Material">
                      {item["Material"]} - {item["Material Description"]}
                    </td>
                    <td data-label="NMO">{item["Sales Order"]}</td>
                    <td data-label="Qty Ordered">{item["Ordered Qty"]}</td>
                    <td data-label="Qty Received">
                      <QuantityContainer>
                        <QuantityText>{totalQuantityByItem(item, "Received Status")}</QuantityText>
                        {(user.role === "tech" || user.role === "warehouseAdmin") && (
                          <PlusButton onClick={() => handleQuantityUpdate(item, "Received")}>+</PlusButton>
                        )}
                      </QuantityContainer>
                    </td>
                    <td data-label="Qty Collected">
                      <QuantityContainer>
                        <QuantityText>{totalQuantityByItem(item, "Collection Status")}</QuantityText>
                        {(user.role === "tech" || user.role === "warehouseAdmin") && (
                          <PlusButton onClick={() => handleQuantityUpdate(item, "Collected")}>+</PlusButton>
                        )}
                      </QuantityContainer>
                    </td>
                    <td data-label="Qty Utilised">
                      <QuantityContainer>
                        <QuantityText>{totalQuantityByItem(item, "Utilisation Status")}</QuantityText>
                        {(user.role === "tech" || user.role === "warehouseAdmin") && (
                          <PlusButton onClick={() => handleQuantityUpdate(item, "Utilised")}>+</PlusButton>
                        )}
                      </QuantityContainer>
                    </td>
                    {(user.role === "tech" || user.role === "warehouseAdmin") && (
                      <td data-label="Stock on Hand">
                        <StockCounter stock={inventoryData[item["Material"]] || 0}>
                          {inventoryData[item["Material"]] || 0}
                        </StockCounter>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </SalesItemsContainer>
        </>
      ) : (
        <p>No order details available.</p>
      )}
    </Container>
  );
};

export default Order;
